import type { CSSProperties } from 'react'
import type { LucideIcon } from 'lucide-react'
import styles from './IconPlaceholder.module.css'

// Family accent palette for the tint — deterministic per seed, like the icon.
// Names are CSS custom properties from globals.css.
const ACCENTS = [
	'--accent-sage',
	'--accent-apricot',
	'--accent-terracotta',
	'--accent-lilac',
	'--accent-sky',
	'--accent-rose',
	'--accent-sunshine',
] as const

// Stable, well-distributed string hash (djb2). Same seed → same icon and tint
// across renders and sessions.
const hash = (s: string): number => {
	let h = 5381
	for (let i = 0; i < s.length; i++) {
		h = ((h << 5) + h + s.charCodeAt(i)) >>> 0
	}
	return h
}

type Props = {
	// Stable string (e.g. an entity id) the icon and tint are derived from, so a
	// given entity always shows the same placeholder.
	seed: string
	// Icons to hash-pick from when `icon` isn't given. Callers narrow this to an
	// on-theme set (activity type, …), or pass `icon` directly and omit it.
	pool?: readonly LucideIcon[]
	// Explicit choice — used as-is instead of hashing the pool.
	icon?: LucideIcon | undefined
}

// Decorative monochrome fallback for cards with no photo. Fills its parent; the
// parent's border-radius/overflow does the clipping. The icon comes from the
// caller's pool (hashed by seed) and the tint from the shared accent palette,
// picked with higher bits so colour and icon vary independently.
export const IconPlaceholder = ({ seed, pool, icon }: Props) => {
	const h = hash(seed)
	const Icon =
		icon ?? (pool && pool.length ? pool[h % pool.length] : undefined)
	const accent =
		ACCENTS[Math.floor(h / ACCENTS.length) % ACCENTS.length] ?? ACCENTS[0]
	return (
		<div
			className={styles.placeholder}
			style={
				{ '--placeholder-accent': `var(${accent})` } as CSSProperties
			}
			aria-hidden="true"
		>
			{Icon ? <Icon className={styles.icon} strokeWidth={1.5} /> : null}
		</div>
	)
}
